import { useState } from "react";
import { Trash, FloppyDisk, Plus } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDate } from "@/lib/api";
import { runwayDelta, deltaText } from "@/lib/scenarios";
import { ScenarioBuilder } from "@/components/dashboard/ScenarioBuilder";
import { useCanEdit } from "@/components/app/TrialBanner";
import { ScenarioNotes, useNoteCounts } from "@/components/dashboard/ScenarioNotes";
import { ScenarioDecision } from "@/components/dashboard/ScenarioDecision";

const Delta = ({ s, base }) => {
  const d = runwayDelta(s, base);
  return <span className={`font-mono text-[11px] ${d >= 0 ? "text-[#00FF9D]" : "text-[#FF5C7A]"}`}>{deltaText(s, base)}</span>;
};

export const ScenarioPanel = ({ draft, saved, selected, lines, toggle, save, remove, baseRunway, setDraft }) => {
  const [building, setBuilding] = useState(false);
  const canEdit = useCanEdit();
  const counts = useNoteCounts();
  const colorOf = (id) => lines.find((l) => l.id === id)?.color;
  return (
    <div data-testid="scenario-panel" className="rounded-xl border border-[#2A2A2A] bg-[#121212]">
      <div className="px-5 py-4 border-b border-[#2A2A2A] flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg">Saved scenarios</h3>
          <p data-testid="scenario-slots" className="text-[11px] text-[#A3A3A3]">{Math.min(saved.length, 3)}/3 slots used · tick up to 3 to compare</p>
        </div>
        {canEdit && <button data-testid="new-scenario-btn" onClick={() => setBuilding((b) => !b)}
          className="flex items-center gap-1 rounded-full border border-[#2A2A2A] px-3 py-1 text-xs hover:border-[#9D00FF] hover:text-[#D9A6FF] transition-colors">
          <Plus size={12} /> New
        </button>}
      </div>
      {building && <ScenarioBuilder onPreview={(s) => { setDraft(s); }} onDone={() => setBuilding(false)} />}
      {draft && (
        <div data-testid="draft-scenario" className="mx-4 mt-4 rounded-lg border border-dashed border-[#9D00FF]/60 bg-[#9D00FF]/[0.06] p-3 flex items-center gap-3">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: colorOf("draft") }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{draft.label} <span className="text-[10px] text-[#A3A3A3]">· unsaved</span></p>
            <Delta s={lines.find((l) => l.id === "draft") || draft} base={baseRunway} />
          </div>
          <button data-testid="save-draft-scenario-btn" onClick={() => save(draft)}
            className="flex items-center gap-1 rounded-full bg-[#9D00FF] px-3 py-1 text-xs text-white hover:shadow-[0_0_16px_rgba(157,0,255,0.6)] transition-shadow">
            <FloppyDisk size={12} /> Save
          </button>
        </div>
      )}
      <div className="p-4 space-y-2">
        {saved.length === 0 && !draft && <p data-testid="no-scenarios" className="text-sm text-[#A3A3A3]">No saved scenarios yet. Ask the Calibrator or tap New.</p>}
        {saved.map((s) => (
          <div key={s.id} data-testid={`saved-scenario-${s.id}`} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-[#1A1A1A] transition-colors">
            <Checkbox data-testid={`scenario-select-${s.id}`} checked={selected.includes(s.id)} onCheckedChange={() => toggle(s.id)}
              className="border-[#3A3A3A] data-[state=checked]:bg-[#00FF9D] data-[state=checked]:text-[#050505]" />
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: colorOf(s.id) || "#2A2A2A" }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{s.label}</p>
              <p className="text-[11px] text-[#A3A3A3]">{s.zero_cash_date ? fmtDate(s.zero_cash_date) : "Not in 24 mo"} · <Delta s={s} base={baseRunway} /></p>
            </div>
            <ScenarioDecision scenario={s} />
            <ScenarioNotes scenario={s} count={counts[s.id]} />
            {canEdit && <button data-testid={`scenario-delete-${s.id}`} onClick={() => remove(s.id)} className="text-[#6B6B6B] hover:text-[#FF5C7A] transition-colors">
              <Trash size={14} />
            </button>}
          </div>
        ))}
      </div>
    </div>
  );
};
